/* Shared math for the FOB Prices module (USDA Market News prices vs GFI's
   own internal invoice/bill prices). Kept in its own file (no UI), same
   convention as seasonComparisonCalc.js.

   Phase 2 — internal-price overlay:
   aggregateInternalPricesByWeek() buckets GFI's own closed invoices/bills
   into ISO weeks so they can be plotted on the same axis as the USDA feed
   (usda_market_prices, see src/supabaseSchema.sql / supabaseDB.js).
   computeSpreadVsUsda() diffs one week's internal price against USDA's. */

/* Same ISO-8601 week algorithm as getISOWeek() in SalesReports.js /
   BillRegistry.js / InvoiceRegistry.js / ShipmentRegistry.js / etc. —
   duplicated here rather than imported, per this codebase's per-file
   convention (see CLAUDE.md), kept byte-identical so FOB Prices weeks
   line up with every other module's week numbering. */
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  var w1 = new Date(d.getFullYear(), 0, 4);
  return String(1 + Math.round(((d - w1) / 864e5 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
}

/* Minimum cases invoiced/billed in a week for that week's weighted-average
   price to count as statistically meaningful — below this the week is
   left out of the result entirely (gap in the line), per the Fase 2 spec. */
export var MIN_CASES_PER_WEEK = 100;

/* Effective date used to bucket a record into an ISO week: invoices carry
   `date`, bills carry `billDate`. Unlike BillRegistry's own week column,
   this intentionally does NOT chase the linked shipment's dateArrival —
   the Fase 2 data source is invoices/bills directly, and pulling in
   gfi-shipments would be a 3rd localStorage read this pure helper (it
   takes its input as a param, it doesn't read localStorage itself)
   shouldn't need. */
function recordDate(rec) {
  return rec.date || rec.billDate || "";
}

/* Per-line unit price: invoice lines carry salePrice (what the client
   pays), bill lines carry reportedPrice (what GFI reports to the
   shipper) — the two fields never coexist on the same line, so trying
   salePrice first and falling back to reportedPrice cleanly handles
   whichever kind of array the caller passed in. */
function linePrice(line) {
  if (line.salePrice !== "" && line.salePrice != null && !isNaN(parseFloat(line.salePrice))) {
    return parseFloat(line.salePrice);
  }
  var rp = parseFloat(line.reportedPrice);
  return isNaN(rp) ? 0 : rp;
}

function matchesFilter(value, filter) {
  if (!filter) return true;
  return String(value || "").toLowerCase() === String(filter).toLowerCase();
}

/* Filters `invoicesOrBills` (gfi-invoices or gfi-bills, or any subset
   already filtered by the caller e.g. by season) down to:
   - closed records only (status === "paid" — the terminal/closing status
     for both invoices [draft/sent/paid] and bills [draft/finalized/paid];
     there is no separate "settled" status in this codebase, "paid" IS
     the closing status for both)
   - lines with saleStatus === "goodDelivery" only (excludes protection,
     pas, dumped, donated, samples, transfer — mirrors the same exclusion
     list used throughout InvoiceRegistry.js/BillRegistry.js/
     seasonComparisonCalc.js)
   - lines matching productFilter/categoryFilter/sizeFilter, when given
     (falsy filter = no restriction on that dimension)

   ...then groups the matching lines by ISO week and computes the
   cases-weighted average price: sum(cases * price) / sum(cases).

   Returns weeks sorted ascending, one row per week that clears
   MIN_CASES_PER_WEEK: { year, weekIso, cases, avgPrice }. A week that
   doesn't clear the threshold is omitted outright (not zero-filled) so
   the caller can render it as a gap in the line, per spec — callers
   don't need to distinguish "no activity" from "not enough activity",
   both mean "don't draw a point here". */
export function aggregateInternalPricesByWeek(invoicesOrBills, productFilter, categoryFilter, sizeFilter) {
  var buckets = {};
  (invoicesOrBills || []).forEach(function (rec) {
    if (!rec || rec.status !== "paid") return;
    var d = recordDate(rec);
    if (!d) return;
    var year = new Date(d + "T00:00:00").getFullYear();
    var weekIso = "W" + getISOWeek(d);
    (rec.lines || []).forEach(function (l) {
      if (!l || l.saleStatus !== "goodDelivery") return;
      var product = l.productName || l.productBase || "";
      if (!matchesFilter(product, productFilter)) return;
      if (!matchesFilter(l.category, categoryFilter)) return;
      if (!matchesFilter(l.size, sizeFilter)) return;
      var cases = parseInt(l.cases, 10) || 0;
      if (cases <= 0) return;
      var price = linePrice(l);
      var key = year + "|" + weekIso;
      if (!buckets[key]) buckets[key] = { year: year, weekIso: weekIso, cases: 0, weightedSum: 0 };
      buckets[key].cases += cases;
      buckets[key].weightedSum += cases * price;
    });
  });
  return Object.keys(buckets)
    .map(function (k) { return buckets[k]; })
    .filter(function (b) { return b.cases >= MIN_CASES_PER_WEEK; })
    .map(function (b) { return { year: b.year, weekIso: b.weekIso, cases: b.cases, avgPrice: b.weightedSum / b.cases }; })
    .sort(function (a, b) {
      if (a.year !== b.year) return a.year - b.year;
      return a.weekIso < b.weekIso ? -1 : a.weekIso > b.weekIso ? 1 : 0;
    });
}

/* Spread between one internal weekly row (from aggregateInternalPricesByWeek
   — needs { avgPrice }) and one USDA weekly row (app-shape record from
   usda_market_prices via supabaseDB.js's mapper — needs { priceLow,
   priceHigh }). USDA never reports a single point price, only a range, so
   its side of the comparison is the range's midpoint; falls back to
   whichever bound is present if only one of priceLow/priceHigh is set.
   Returns null when there's nothing usable to compare (missing internal
   price, or USDA row with neither bound) so callers can render "—"
   instead of NaN. `favorable` is true when GFI's price is at or above
   USDA's (green in the spec's cross-check card; false -> red). */
export function computeSpreadVsUsda(internalRow, usdaRow) {
  if (!internalRow || !usdaRow) return null;
  var mine = parseFloat(internalRow.avgPrice);
  if (isNaN(mine)) return null;
  var lo = parseFloat(usdaRow.priceLow);
  var hi = parseFloat(usdaRow.priceHigh);
  var usda;
  if (!isNaN(lo) && !isNaN(hi)) usda = (lo + hi) / 2;
  else if (!isNaN(lo)) usda = lo;
  else if (!isNaN(hi)) usda = hi;
  else return null;
  var spread = mine - usda;
  var spreadPct = usda !== 0 ? (spread / usda) * 100 : null;
  return { spread: spread, spreadPct: spreadPct, mine: mine, usda: usda, favorable: spread >= 0 };
}
